/**
 * useRemoteAdvanceTimer — Schedules track advance for remote renderer playback.
 *
 * When audio plays on a network renderer (UPnP, RAAT, AirPlay, etc.), the web client
 * has no <audio> element ticking — WebAudioRenderer is paused. Track advance cannot
 * rely on the renderer's STOPPED events because they're ambiguous (track finished vs
 * renderer transitioning vs user stopped).
 *
 * Instead, we use the same approach as iOS NowPlayingManager: schedule a timer for
 * (duration - position) seconds after playback starts. When it fires, advance.
 *
 * Timer lifecycle:
 *   playbackState → 'playing' + remote renderer  →  start timer
 *   playbackState → 'paused'                     →  pause timer (save remaining)
 *   playbackState → 'stopped' or 'idle'          →  cancel timer
 *   currentTrack changes                          →  cancel + restart for new duration
 *   seek (position jumps)                         →  recalculate remaining, restart timer
 */
import { useEffect, useRef } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { isTestPlaybackMode } from '../transport/QueueController';
import { isIOSNativeApp } from '../utils/platform';

function isNetworkRenderer(zone: any): boolean {
  if (!zone || !zone.type) return false;
  const localTypes = ['browser', 'local', 'local-native'];
  const localIds = ['this_computer', 'local_computer', 'this_device'];
  return !localTypes.includes(zone.type) && !localIds.includes(zone.id);
}

export function useRemoteAdvanceTimer(): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedAtRef = useRef<number>(0);
  const remainingRef = useRef<number>(0);
  const activeTrackIdRef = useRef<string | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = (remainingSecs: number) => {
    clearTimer();
    if (remainingSecs <= 0) {
      // Track should have already ended — advance immediately
      if (remainingSecs > -5 && activeTrackIdRef.current) {
        import('../transport/QueueController').then(({ QueueController }) => {
          QueueController.next();
        }).catch(e => console.error('[RemoteAdvanceTimer] Advance failed:', e));
      }
      return;
    }

    remainingRef.current = remainingSecs;
    startedAtRef.current = Date.now();

    const timedForTrackId = activeTrackIdRef.current;
    timerRef.current = setTimeout(async () => {
      timerRef.current = null;

      const currentId = useMaestroStore.getState().currentTrack?.id || null;
      if (currentId !== timedForTrackId) {
        return;
      }

      // Observer devices should NOT advance tracks locally — the controller
      // will push the next track via sync. Wait up to 8s for a sync event
      // carrying a new track before falling through to local advance.
      const sync = getSyncHandler();
      if (sync.isObserving()) {
        const waitStart = Date.now();
        while (Date.now() - waitStart < 8000) {
          await new Promise(r => setTimeout(r, 1000));
          const nowId = useMaestroStore.getState().currentTrack?.id || null;
          if (nowId !== timedForTrackId) {
            return;
          }
        }
        return;
      }

      if (isTestPlaybackMode()) {
        return;
      }

      try {
        const { QueueController } = await import('../transport/QueueController');
        QueueController.next();
      } catch (e) {
        console.error('[RemoteAdvanceTimer] Advance failed:', e);
      }
    }, remainingSecs * 1000);
  };

  const pauseTimer = () => {
    if (!timerRef.current) return;
    const elapsed = (Date.now() - startedAtRef.current) / 1000;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
    clearTimer();
  };

  useEffect(() => {
    // On iOS native app with a network renderer, this timer is DISABLED.
    // NativeQueueManager owns track advancement — its native timer is not
    // subject to WKWebView throttling (screen lock, background, etc.).
    // On desktop/browser with a network renderer, this timer is the primary
    // advance mechanism since there's no native fallback.

    const unsubscribe = useMaestroStore.subscribe((state, prevState) => {
      // Discovery may refresh `discoveredZones` often (iOS renderer poll, SSDP, etc.).
      // Skip timer logic when only the zone list changed — same active zone + same transport slice.
      if (prevState) {
        const sameActiveZoneId = state.discovery?.activeZone?.id === prevState.discovery?.activeZone?.id;
        const sameCore =
          sameActiveZoneId
          && state.playbackState === prevState.playbackState
          && state.activeSource === prevState.activeSource
          && state.currentTrack?.id === prevState.currentTrack?.id
          && state.queueIndex === prevState.queueIndex
          && state.queue.length === prevState.queue.length
          && state.duration === prevState.duration
          && state.position === prevState.position;
        const listOnly =
          state.discovery?.discoveredZones !== prevState.discovery?.discoveredZones;
        if (sameCore && listOnly) {
          return;
        }
      }

      const zone = state.discovery?.activeZone;
      const isRemote = isNetworkRenderer(zone);
      const playbackState = state.playbackState;
      const prevPlaybackState = prevState?.playbackState;
      const track = state.currentTrack;
      const trackId = track?.id || null;
      const prevTrackId = prevState?.currentTrack?.id || null;
      const duration = state.duration || 0;
      const position = state.position || 0;

      if (!isRemote) {
        if (timerRef.current) {
          clearTimer();
          activeTrackIdRef.current = null;
        }
        return;
      }

      // Radio sources (both live Icecast streams and Ella Radio generated playlists)
      // have their own advancement mechanisms. The timer must NOT fire for radio.
      if (state.activeSource === 'radio') {
        if (timerRef.current) {
          clearTimer();
          activeTrackIdRef.current = null;
        }
        return;
      }

      // On iOS, NativeQueueManager owns track advancement for network renderers.
      // Web-side setTimeout is unreliable when iOS throttles the WKWebView
      // (screen locked, app backgrounded) — timers get coalesced or delayed,
      // causing premature advances, track skipping, and dead gaps.
      // The native timer + NativeQueueManager handle this without the WebView.
      if (isIOSNativeApp() && isRemote) {
        if (timerRef.current) {
          clearTimer();
          activeTrackIdRef.current = null;
        }
        return;
      }

      // Track changed — restart timer for new track.
      // CRITICAL: use track.duration (metadata) instead of state.duration (store)
      // because the store's duration/position may still reflect the PREVIOUS track
      // when the track ID changes. This caused premature advance (2s timer instead
      // of full track duration) when the native side reconciled track changes.
      if (trackId !== prevTrackId && trackId !== null) {
        activeTrackIdRef.current = trackId;
        clearTimer();
        const rawDuration = track?.duration || 0;
        const trackDuration = rawDuration > 10000 ? rawDuration / 1000 : rawDuration;
        if (playbackState === 'playing' && trackDuration > 0) {
          startTimer(trackDuration + 2);
        }
        return;
      }

      // Playback state changed
      if (playbackState !== prevPlaybackState) {
        if (playbackState === 'playing' && track && duration > 0) {
          if (remainingRef.current > 0 && prevPlaybackState === 'paused') {
            startTimer(remainingRef.current);
          } else {
            const remaining = duration - position;
            startTimer(remaining + 2);
          }
        } else if (playbackState === 'paused') {
          pauseTimer();
        } else if (playbackState === 'stopped' || playbackState === 'idle') {
          // Renderer reported stopped while we had an active timer for this track.
          // The timer hasn't fired yet (race: renderer finishes before our +2s buffer).
          // Only advance if the track is near completion — a "stopped" event early
          // in the track is a transient renderer state (buffering, URI swap), NOT
          // a completed track. Without this guard, UPnP renderers that briefly
          // report STOPPED during initial load cause a spurious next() call.
          const hadActiveTimer = timerRef.current !== null && activeTrackIdRef.current === trackId;
          clearTimer();
          remainingRef.current = 0;
          if (hadActiveTimer && prevPlaybackState === 'playing' && trackId) {
            const trackIsNearEnd = duration > 0 && position > 0 && (duration - position) < 30;
            if (isTestPlaybackMode()) {
              // Suppressed during test playback
            } else if (getSyncHandler().isObserving()) {
              // Observer waits for controller
            } else if (!trackIsNearEnd) {
              // Stopped early in track — suppressed (transient renderer state)
            } else {
              import('../transport/QueueController').then(({ QueueController }) => {
                QueueController.next();
              }).catch(e => console.error('[RemoteAdvanceTimer] Advance failed:', e));
            }
          }
        }
        return;
      }

      // Seek detection: position jumped significantly while playing.
      // Only recalculate for genuine user seeks (large jumps). Sync position
      // updates arrive every ~10s and cause small jumps that are NOT seeks.
      // Threshold of 15s filters out sync noise while catching real seeks.
      if (playbackState === 'playing' && track && duration > 0) {
        const prevPosition = prevState?.position || 0;
        const delta = Math.abs(position - prevPosition);
        if (delta > 15) {
          const remaining = duration - position;
          if (remaining > 5) {
            startTimer(remaining + 2);
          }
        }
      }
    });

    return () => {
      unsubscribe();
      clearTimer();
    };
  }, []);
}
